import React, { Component } from 'react'
import PropTypes from 'prop-types'
import Alert from 'react-s-alert'
import createMentionPlugin, {
  defaultSuggestionsFilter,
} from 'draft-js-mention-plugin'
import 'draft-js-mention-plugin/lib/plugin.css'
import getMuiTheme from 'material-ui/styles/getMuiTheme'
import isUsingOldWebKit, { MACOS_VERSION_ERROR } from './check-user-agent'
import { logException } from '../../services/sentry'
import { prepareMentionableData } from '../../services/draft-state/mentionable'
import { processIncoming, serializeSetting } from '../../services/draft-state'

const buildTextEditor = WrappedComponent =>
  class extends Component {
    static childContextTypes = { muiTheme: PropTypes.object }

    static propTypes = {
      closePopover: PropTypes.func,
      discardDrag: PropTypes.func,
      handleSnackbarOpen: PropTypes.func,
      hasPlum: PropTypes.bool,
      hasReviews: PropTypes.bool,
      isEmail: PropTypes.bool,
      isRecall: PropTypes.bool,
      isReminder: PropTypes.bool,
      pmsType: PropTypes.string,
      pmsVersion: PropTypes.number,
      selectedApptTypes: PropTypes.arrayOf(
        PropTypes.shape({
          appointment_type_guid: PropTypes.string,
          code: PropTypes.string,
          created_at: PropTypes.string,
          description: PropTypes.string,
          id: PropTypes.string,
          online_scheduling: PropTypes.bool,
          practice_id: PropTypes.number,
          updated_at: PropTypes.string,
          visibility: PropTypes.string,
        }),
      ),
      setting: PropTypes.shape({
        always_send: PropTypes.bool,
        body: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.shape({
            text: PropTypes.string,
          }),
        ]),
        id: PropTypes.number,
        offset: PropTypes.number,
        practice: PropTypes.shape({
          pms_type: PropTypes.string,
        }),
        subject: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.shape({
            text: PropTypes.string,
          }),
        ]),
      }).isRequired,
      setToInactive: PropTypes.func,
    }

    constructor(props) {
      super(props)

      const {
        isEmail,
        isRecall,
        hasPlum,
        hasReviews,
        pmsType,
        pmsVersion,
        isTemplate,
        setting: {
          subject,
          body,
          id: settingId,
          always_send: shouldAlwaysSend,
          template_name,
        },
      } = this.props

      const { bodyState, subjectState } = processIncoming(body, subject, {
        isInDB: !!settingId,
        isEmail,
      })
      this.mentionPlugin = createMentionPlugin()
      this.generateMentionableData = prepareMentionableData(
        !!isRecall,
        !!hasPlum,
        !!hasReviews,
        pmsType,
        pmsVersion,
        isTemplate,
      )
      this.state = {
        bodyState,
        subjectState,
        mentionableData: this.generateMentionableData,
        shouldAlwaysSend,
        templateName: template_name,
        isTooltipActive: false,
      }
    }

    getChildContext() {
      return { muiTheme: getMuiTheme() }
    }

    componentDidMount() {
      if (isUsingOldWebKit()) {
        Alert.warning(MACOS_VERSION_ERROR, {
          postion: 'top-right',
          effect: 'jelly',
          timeout: 15000,
          html: true,
        })
      }
    }

    componentWillReceiveProps({
      setting: {
        subject,
        body,
        id: nextId,
        always_send: shouldAlwaysSend,
        template_name,
      },
    }) {
      const {
        isEmail,
        isRecall,
        hasPlum,
        hasReviews,
        pmsType,
        pmsVersion,
        isTemplate,
        setting: { id },
      } = this.props
      if (id !== nextId) {
        const { bodyState, subjectState } = processIncoming(body, subject, {
          isInDB: !!nextId,
          isEmail,
          isTemplate,
        })
        this.mentionPlugin = createMentionPlugin()
        this.generateMentionableData = prepareMentionableData(
          !!isRecall,
          !!hasPlum,
          !!hasReviews,
          pmsType,
          pmsVersion,
          isTemplate,
        )
        this.setState({
          bodyState,
          subjectState,
          mentionableData: this.generateMentionableData,
          shouldAlwaysSend,
          templateName: template_name,
          isTooltipActive: false,
        })
      }
    }

    updateBodyState = bodyState => {
      this.setState({ bodyState })
    }

    updateSubjectState = subjectState => {
      this.setState({ subjectState })
    }

    updateTemplateName = e => {
      const enteredName = e.target.value
      this.setState({ templateName: enteredName })
    }

    handleSave = isTemplate => {
      const { setting, selectedApptTypes } = this.props
      const { id: settingId } = setting
      const { shouldAlwaysSend, templateName } = this.state

      const { body, subject } = this.extractContents()

      if (isTemplate) {
        const saveAction = settingId ? 'updateTemplate' : 'saveTemplate'
        return this.props[saveAction]({
          body,
          subject,
          templateName,
          settingId,
        }).catch(err => logException(err))
      } else {
        const saveAction = settingId // is it an existing setting?
          ? 'updateSetting'
          : 'createSetting'

        return this.props[saveAction](
          {
            body,
            subject,
            always_send: shouldAlwaysSend,
            appointment_types: selectedApptTypes
              ? selectedApptTypes.map(type => type.id)
              : [],
          },
          settingId,
        ).catch(err => logException(err))
      }
    }

    handleDelete = () => {
      const {
        setting: { id: settingId },
        handleSnackbarOpen,
        setToInactive,
        closePopover,
      } = this.props
      this.setState({ isEditing: false })
      if (settingId) {
        // sorry, no undos for 'unsaved' message settings
        handleSnackbarOpen(settingId)
      }
      setToInactive(settingId)
      closePopover()
    }

    handleDiscard = () => {
      const { closePopover, discardDrag } = this.props
      discardDrag()
      closePopover()
    }

    extractContents = () => {
      const { bodyState, subjectState } = this.state
      const { isEmail } = this.props

      return serializeSetting(bodyState, subjectState, { isEmail })
    }

    handleSearchChange = ({ value }) => {
      const mentionableData = defaultSuggestionsFilter(
        value,
        this.generateMentionableData,
      )
      this.setState({ mentionableData })
    }

    handleCheckbox = () => {
      const shouldAlwaysSend = !this.state.shouldAlwaysSend
      this.setState({ shouldAlwaysSend })
    }

    toggleTooltip = () => {
      const isTooltipActive = !this.state.isTooltipActive
      this.setState({ isTooltipActive })
    }

    render() {
      const mentionPlugin = this.mentionPlugin
      const { MentionSuggestions } = mentionPlugin
      return (
        <WrappedComponent
          {...this.props}
          {...this.state}
          handleCheckbox={this.handleCheckbox}
          handleDelete={this.handleDelete}
          handleDiscard={this.handleDiscard}
          handleFocus={this.handleFocus}
          handleSave={this.handleSave}
          handleSearchChange={this.handleSearchChange}
          MentionSuggestions={MentionSuggestions}
          updateBodyState={this.updateBodyState}
          updateSubjectState={this.updateSubjectState}
          updateTemplateName={this.updateTemplateName}
          plugins={[mentionPlugin]}
          toggleTooltip={this.toggleTooltip}
        />
      )
    }
  }

export default buildTextEditor



// WEBPACK FOOTER //
// ./src/components/Settings/buildTextEditor.js